// App mode controls whether seed data, marketing fixtures, and mock screens
// are rendered. Three modes:
//
//   production (DEFAULT)
//     The shipping experience. Brand-new user, empty roster, no fixtures.
//     Used by both native iOS / Android builds and the web prod build.
//     Designers and screenshot pipelines opt out via the hash overrides
//     below.
//
//   demo
//     Designers + stakeholders walking the app for review. Pre-populated
//     box, sample battle team, sample friends. Opt in via #mode=demo or
//     the in-app Settings toggle.
//
//   screenshot
//     App Store asset capture. Same data shape as demo but every species
//     name is replaced with a generic "Specimen NNNN" label so submission
//     assets reveal zero TPCi creative elements. Opt in via
//     #mode=screenshot.
//
// As of beta, production is the only mode that ever ships to real users.
// Demo and screenshot are internal-only opt-ins.

import { useState, useEffect } from "react";

export type AppMode = "demo" | "production" | "screenshot";

// Resolve the initial mode for this session. Order of precedence:
//   1. URL hash (#mode=demo / #mode=screenshot)
//   2. Native shell injection (window.__NEXUS_APP_MODE__) for the
//      Capacitor iOS / Android bundles.
//   3. Default to production.
function readInitialMode(): AppMode {
  if (typeof window === "undefined") return "production";
  const hash = window.location.hash || "";
  if (hash.includes("mode=demo")) return "demo";
  if (hash.includes("mode=production")) return "production";
  if (hash.includes("mode=screenshot")) return "screenshot";
  // Native shell injection for Capacitor builds.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.__NEXUS_APP_MODE__) return w.__NEXUS_APP_MODE__ as AppMode;
  return "production";
}

let currentMode: AppMode = readInitialMode();
const listeners = new Set<(m: AppMode) => void>();

// Defensive guard: a production build that boots in any mode other than
// "production" means main.tsx's PROD-mode hook (or the Capacitor native
// hook) failed to fire. Should never happen; if it does, we want a
// visible warning in browser DevTools so the regression is caught in QA
// before users see seed data.
if (import.meta.env.PROD && currentMode !== "production") {
  // eslint-disable-next-line no-console
  console.warn(
    `[NEXUS] Production build running in '${currentMode}' mode. ` +
    `Expected 'production'. Seed data should still be empty thanks to ` +
    `inProductionMode() gates, but this signals a bootstrap regression.`,
  );
}

export function getAppMode(): AppMode {
  return currentMode;
}

export function setAppMode(m: AppMode) {
  currentMode = m;
  listeners.forEach((l) => l(m));
}

export function subscribeAppMode(l: (m: AppMode) => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}



// Returns true when the app should hide all demo seed data (the "production"
// experience for a brand new user with an empty roster).
export function inProductionMode(): boolean {
  return currentMode === "production";
}

// Returns the user-facing name to show for a species id, applying screenshot
// mode placeholders if appropriate.
//
// In screenshot mode every species rendered as text becomes "Specimen NNNN".
// The glyph still renders (it shows a 3-letter mark derived from the real
// name), but the *written* name in headings, list rows and chat is hidden,
// which is exactly what the audit requires for App Store assets.
export function displayName(realName: string, dexNumber: number | undefined): string {
  if (currentMode === "screenshot" && dexNumber != null) {
    return `Specimen ${dexNumber.toString().padStart(4, "0")}`;
  }
  return realName;
}
